import React, { memo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProfileDropdown from './Auth/ProfileDropdown';
import chatService from '../services/chat.service';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import './Header.css';

interface HeaderProps {
  darkMode: boolean;
  onDarkModeToggle: () => void;
  onMobileMenuToggle?: () => void;
  onLoginClick?: () => void;
  onSignupClick?: () => void;
}

const Header: React.FC<HeaderProps> = memo(({
  darkMode,
  onDarkModeToggle,
  onMobileMenuToggle,
  onLoginClick,
  onSignupClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      chatService.getUnreadCount().then(setUnreadCount);
    }
  }, [isAuthenticated, location.pathname]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/vacancies', label: 'Browse Vacancies' },
    { path: '/companies', label: 'Companies' },
    ...(isAuthenticated
      ? [
          { path: '/post-vacancy', label: 'Post Vacancy' },
          { path: '/my-referrals', label: 'My Referrals' },
        ]
      : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleNavigate = (page: string) => {
    navigate(page);
  };

  return (
    <div className={`header-container ${darkMode ? 'dark' : 'light'}`}>
      <header className={`header ${darkMode ? 'dark' : 'light'}`}>
        {onMobileMenuToggle && (
          <button className="mobile-menu-btn" onClick={onMobileMenuToggle} aria-label="Toggle menu">
            <MenuIcon />
          </button>
        )}

        <div className="logo" onClick={() => navigate('/')} role="button" tabIndex={0}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Jobometry</span>
        </div>

        <ul className="nav-tabs">
          {navItems.map(item => (
            <li
              key={item.path}
              className={`nav-tab ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <span className="nav-text">{item.label}</span>
            </li>
          ))}
        </ul>

        <div className="header-right">
          {isAuthenticated && (
            <button
              className="header-chat-btn"
              onClick={() => navigate('/messages')}
              aria-label="Messages"
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
              {unreadCount > 0 && <span className="chat-unread-badge">{unreadCount}</span>}
            </button>
          )}

          <button
            className="dark-mode-toggle"
            onClick={onDarkModeToggle}
            aria-label="Toggle dark mode"
          >
            {darkMode ? <LightModeOutlinedIcon sx={{ fontSize: 20 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />}
          </button>

          <div className="auth-buttons">
            {!isAuthenticated ? (
              <>
                <button className="auth-btn login-btn" onClick={onLoginClick}>Login</button>
                <button className="auth-btn signup-btn" onClick={onSignupClick}>Sign Up</button>
              </>
            ) : (
              <div className="profile-dropdown-container">
                <button
                  className="profile-btn"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  aria-label="User profile menu"
                >
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.name} className="profile-avatar" />
                  ) : (
                    <div className="profile-avatar-placeholder">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                </button>
                <ProfileDropdown
                  isOpen={showProfileDropdown}
                  onClose={() => setShowProfileDropdown(false)}
                  onNavigate={handleNavigate}
                  darkMode={darkMode}
                />
              </div>
            )}
          </div>
        </div>
      </header>
    </div>
  );
});

Header.displayName = 'Header';
export default Header;
